import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import StationMapDashboardPanel from 'station/components/dashboard/component/map/StationMapDashboardPanel'
import { compact, isUndefined, uniq } from 'lodash'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import DtoFile from 'components/file/dto/DtoFile'
import { DOCUMENT, PICTURE } from 'components/file/constant/FileConstants'
import SmallPicturePanel from 'components/picture/SmallPicturePanel'
import FilePanel from 'components/file/FilePanel'
import StationAction from 'station/actions/StationAction'
import moment from 'moment'
import { getFileExtension, getFileName, getFormatedFileName } from 'utils/FileUtils'
import ToastrAction from 'toastr/actions/ToastrAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import WatershedAction from 'referencial/components/watershed/actions/WatershedAction'
import PropTypes from 'prop-types'
import DtoQualitometer from 'quality/dto/DtoQualitometer'

const CARTO_MNEMONIQUUE = 'CAR'
const IS_ACTIVE = '1'

const CatchmentMapDashboardPanel = ({
    isLoaded,
    onLoaded,
    qualitometersLinked = [],
    defaultSelectedPoint = {},
}) => {
    const dispatch = useDispatch()

    const {
        catchment,
        files,
        pictures,
        watersheds,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        files: store.StationReducer.files,
        pictures: store.StationReducer.pictures,
        watersheds: store.WatershedReducer.watersheds,
    }), shallowEqual)

    const [kmlFiles, setKmlFiles] = useState([])

    useEffect(() => {
        dispatch(WatershedAction.fetchWatersheds())
    }, [dispatch])

    const qualitoWatersheds = useMemo(() => compact(
        uniq(qualitometersLinked.flatMap(q => [q.watershed, q.watershed2, q.watershed3, q.watershed4]))
            .map(id => watersheds.find(w => w.id === id)?.code ?? ''),
    ), [qualitometersLinked, watersheds])

    const handleRawDocuments = useCallback((documents) => {
        return documents.map(d => {
            if (isUndefined(d?.name) || isUndefined(d?.url)) return undefined
            if (kmlFiles.some(f => f.url === d.url)) return undefined
            const now = moment().valueOf()
            const formattedName = getFormatedFileName(catchment.code ?? '', IS_ACTIVE, getFileName(d.name), getFileExtension(d.name), CARTO_MNEMONIQUUE, now, STATION_TYPE_NAME.catchment)
            if (files.some(f => f.name === formattedName)) {
                dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
                return undefined
            }
            const formattedFile = {
                url: d.url,
                name: formattedName,
                stationCode: catchment.code ?? '',
            }
            return new DtoFile(formattedFile, DOCUMENT)
        })
    }, [catchment.code])

    const getAacKml = useCallback((specifiedFiles = undefined) => {
        const usedFiles = specifiedFiles ?? files
        const kmlAreaFiles = usedFiles.filter(f => f.type === CARTO_MNEMONIQUUE && f.name?.endsWith('.kml') && f.active === IS_ACTIVE).map(f => f.name)
        if (catchment.sandreCode && kmlAreaFiles.length === 0) {
            dispatch(CatchmentAction.fetchKML(`cdaac_${catchment.sandreCode}`))
                .then((document = undefined) => {
                    if (!isUndefined(document)) {
                        const formattedFile = handleRawDocuments([document])[0]
                        if (isUndefined(formattedFile)) return
                        setKmlFiles(prev => [...prev, { ...formattedFile, aacFromSandre: true }])
                    }
                })
                .finally(onLoaded)
        } else {
            onLoaded()
        }
    }, [dispatch, catchment.sandreCode, handleRawDocuments])

    const getWatermassKml = useCallback(() => {
        dispatch(CatchmentAction.fetchWatermassKML(catchment.meCode?.replace('FR', '')))
            .then((document = undefined) => {
                if (!isUndefined(document)) {
                    const formattedFile = handleRawDocuments([document])[0]
                    if (isUndefined(formattedFile)) return
                    setKmlFiles(prev => [...prev, { ...formattedFile, watermassFromSandre: true }])
                }
            })
    }, [dispatch, catchment.meCode, handleRawDocuments])

    const getStationWatershedKml = useCallback(() => {
        WatershedAction.promiseStationWatershed(defaultSelectedPoint.x, defaultSelectedPoint.y).then(json => {
            if (json?.code) {
                dispatch(CatchmentAction.fetchWatershedKML(json.code))
                    .then((document = undefined) => {
                        if (!isUndefined(document)) {
                            const formattedFile = handleRawDocuments([document])[0]
                            if (isUndefined(formattedFile)) return
                            setKmlFiles(prev => [...prev, { ...formattedFile, stationWatershedFromCoordinates: true }])
                        }
                    })
            }
        })
    }, [dispatch, defaultSelectedPoint, handleRawDocuments])

    const getWatershedsKml = useCallback(() => {
        const watershedsFiles = qualitoWatersheds.map(code => dispatch(CatchmentAction.fetchWatershedKML(code)))
        Promise.all(watershedsFiles)
            .then((documents = []) => {
                if (compact(documents).length > 0) {
                    const formattedFiles = handleRawDocuments(documents)
                    setKmlFiles(prev => [...prev, ...compact(formattedFiles.map(f => ({ ...f, watershedFromSandre: true })))])
                }
            })
    }, [dispatch, qualitoWatersheds, handleRawDocuments])

    useEffect(() => {
        if (catchment.meCode) getWatermassKml()
        if (!isUndefined(defaultSelectedPoint?.x) && !isUndefined(defaultSelectedPoint?.y)) getStationWatershedKml()
        if (qualitoWatersheds.length > 0) getWatershedsKml()
        if (!catchment.code) {
            dispatch(StationAction.resetDocuments())
        }
        if (!catchment.code || files.length > 0 && files[0].stationCode === catchment.code) {
            getAacKml()
            return
        }
        Promise.all([
            dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment))
                .then((documents = []) => {
                    const formattedDocuments = documents.map(document => new DtoFile(document, DOCUMENT))
                    getAacKml(formattedDocuments)
                }),
            dispatch(StationAction.fetchPictures(catchment.code, STATION_TYPE_NAME.catchment)),
        ])
    }, [dispatch, catchment.code, catchment.meCode, qualitoWatersheds, getAacKml, getWatermassKml, getWatershedsKml, getStationWatershedKml, defaultSelectedPoint])

    const docs = useMemo(() => files.filter(f => f.fileType === DOCUMENT), [files])
    const imgs = useMemo(() => pictures.filter(f => f.fileType === PICTURE), [pictures])

    return (
        <>
            <StationMapDashboardPanel
                noMarkerTooltip
                station={defaultSelectedPoint}
                type={defaultSelectedPoint.typeName}
                specificFiles={kmlFiles}
            />
            { (isLoaded && !!imgs?.length) && (<SmallPicturePanel pictures={imgs} />)}
            { (isLoaded && !!docs?.length) && (<FilePanel code={catchment.code} files={docs} hideTitle />)}
        </>
    )
}

CatchmentMapDashboardPanel.propTypes = {
    isLoaded: PropTypes.bool,
    onLoaded: PropTypes.func,
    qualitometersLinked: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometer)),
    defaultSelectedPoint: PropTypes.instanceOf(DtoQualitometer),
}

export default CatchmentMapDashboardPanel